import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { useTour } from '@/contexts/TourContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import { ScrollArea } from '@/components/ui/scroll-area';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogFooter,
} from '@/components/ui/dialog';
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
} from '@/components/ui/sheet';
import { 
  Gauge,
  Plus, 
  Trash2, 
  Edit3,
  MapPin,
  Eye,
  Loader2,
  Hash,
  FileText,
  Search,
  Filter,
  Package,
  Building,
  AlertCircle,
  Thermometer,
  Droplets,
  Wind,
  Activity
} from 'lucide-react';
import { cn } from '@/lib/utils';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Instrumentation type options
const INSTRUMENTATION_TYPES = [
  // Pressure
  { value: 'pressure_transmitter', label: 'Pressure Transmitter', icon: Gauge },
  { value: 'pressure_gauge', label: 'Pressure Gauge', icon: Gauge },
  { value: 'dp_transmitter', label: 'Differential Pressure Transmitter', icon: Gauge },
  // Temperature
  { value: 'rtd', label: 'Temperature Sensor (RTD)', icon: Thermometer },
  { value: 'thermocouple', label: 'Temperature Sensor (Thermocouple)', icon: Thermometer },
  // Flow
  { value: 'flow_magnetic', label: 'Flow Meter (Magnetic)', icon: Droplets },
  { value: 'flow_ultrasonic', label: 'Flow Meter (Ultrasonic)', icon: Droplets },
  { value: 'flow_turbine', label: 'Flow Meter (Turbine)', icon: Droplets },
  { value: 'flow_coriolis', label: 'Flow Meter (Coriolis)', icon: Droplets },
  // Level
  { value: 'level_radar', label: 'Level Sensor (Radar)', icon: Activity },
  { value: 'level_ultrasonic', label: 'Level Sensor (Ultrasonic)', icon: Activity },
  { value: 'level_float', label: 'Level Sensor (Float)', icon: Activity },
  { value: 'level_dp', label: 'Level Sensor (DP)', icon: Activity },
  // Vibration & Speed
  { value: 'vibration', label: 'Vibration Sensor', icon: Activity },
  { value: 'speed_rpm', label: 'Speed / RPM Sensor', icon: Activity },
  // Valves & Actuators
  { value: 'valve_actuator', label: 'Valve Actuator', icon: Wind },
  { value: 'control_valve', label: 'Control Valve', icon: Gauge },
  { value: 'safety_valve', label: 'Safety Relief Valve', icon: AlertCircle },
  // Safety Devices
  { value: 'esd_button', label: 'ESD Button', icon: AlertCircle },
  { value: 'fire_extinguisher', label: 'Fire Extinguisher', icon: AlertCircle },
  { value: 'smoke_detector', label: 'Smoke Detector', icon: AlertCircle },
  { value: 'gas_detector', label: 'Gas Detector', icon: Wind },
  // Other
  { value: 'analyzer', label: 'Analyzer', icon: Activity },
  { value: 'other', label: 'Other', icon: Gauge },
];

// Parent type options
const PARENT_TYPES = [
  { value: 'asset', label: 'Asset' },
  { value: 'building', label: 'Building' },
];

export function InstrumentationPanel({ 
  isOpen,
  onClose,
  currentSweep,
  cameraPosition,
  onNavigateToInstrument
}) {
  const { token, canEdit, isViewer } = useAuth();
  const { currentTour } = useTour();
  const [instruments, setInstruments] = useState([]);
  const [assets, setAssets] = useState([]);
  const [buildings, setBuildings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [typeFilter, setTypeFilter] = useState('all');
  const [parentFilter, setParentFilter] = useState('all');
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [editingInstrument, setEditingInstrument] = useState(null);
  const [formData, setFormData] = useState({
    tag_number: '',
    name: '',
    instrument_type: '',
    description: '',
    manufacturer: '',
    model: '',
    range: '',
    parent_type: 'asset',
    parent_id: '',
    capturePosition: true,
  });

  // Fetch instruments
  const fetchInstruments = useCallback(async () => {
    if (!currentTour?.id || !token) return;
    
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/instrumentation?tour_id=${currentTour.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setInstruments(data);
      }
    } catch (error) {
      console.error('Error fetching instruments:', error);
    } finally {
      setLoading(false);
    }
  }, [currentTour?.id, token]);

  // Fetch assets for parent selection
  const fetchAssets = useCallback(async () => {
    if (!currentTour?.id || !token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/equipment/tour/${currentTour.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setAssets(data);
      }
    } catch (error) {
      console.error('Error fetching assets:', error);
    }
  }, [currentTour?.id, token]);

  // Fetch buildings for parent selection
  const fetchBuildings = useCallback(async () => {
    if (!currentTour?.id || !token) return;
    
    try {
      const response = await fetch(`${API_URL}/api/buildings?tour_id=${currentTour.id}`, {
        headers: { 'Authorization': `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setBuildings(data);
      }
    } catch (error) {
      console.error('Error fetching buildings:', error);
    }
  }, [currentTour?.id, token]);

  useEffect(() => {
    if (isOpen) {
      fetchInstruments();
      fetchAssets();
      fetchBuildings();
    }
  }, [isOpen, fetchInstruments, fetchAssets, fetchBuildings]);

  // Filter instruments
  const filteredInstruments = instruments.filter(instrument => {
    const matchesSearch = instrument.tag_number?.toLowerCase().includes(searchTerm.toLowerCase()) ||
                         instrument.name?.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesType = typeFilter === 'all' || instrument.instrument_type === typeFilter;
    const matchesParent = parentFilter === 'all' || instrument.parent_type === parentFilter;
    return matchesSearch && matchesType && matchesParent;
  });

  // Handle form submit
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!token || !currentTour?.id) return;

    try {
      const url = editingInstrument 
        ? `${API_URL}/api/instrumentation/${editingInstrument.id}`
        : `${API_URL}/api/instrumentation`;
      
      const method = editingInstrument ? 'PUT' : 'POST';
      
      const submitData = {
        ...formData,
        tour_id: currentTour.id,
      };

      // Only include position and rotation if capturePosition is enabled and we have sweep data
      if (formData.capturePosition && currentSweep?.id) {
        submitData.sweep_id = currentSweep.id;
        if (cameraPosition?.position) {
          submitData.position = cameraPosition.position;
        }
        if (cameraPosition?.rotation) {
          submitData.rotation = cameraPosition.rotation;
        }
      }

      // Remove capturePosition from submission (it's frontend-only)
      delete submitData.capturePosition;
      
      const response = await fetch(url, {
        method,
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify(submitData)
      });

      if (response.ok) {
        await fetchInstruments();
        handleCloseDialog();
      }
    } catch (error) {
      console.error('Error saving instrument:', error);
    }
  };

  // Handle delete
  const handleDelete = async (instrumentId) => {
    if (!token || !window.confirm('Are you sure you want to delete this instrument?')) return;

    try {
      const response = await fetch(`${API_URL}/api/instrumentation/${instrumentId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${token}` }
      });

      if (response.ok) {
        await fetchInstruments();
      }
    } catch (error) {
      console.error('Error deleting instrument:', error);
    }
  };

  // Handle dialog close
  const handleCloseDialog = () => {
    setIsCreateDialogOpen(false);
    setEditingInstrument(null);
    setFormData({
      tag_number: '',
      name: '',
      instrument_type: '',
      description: '',
      manufacturer: '',
      model: '',
      range: '',
      parent_type: 'asset',
      parent_id: '',
      capturePosition: true,
    });
  };

  // Handle edit
  const handleEdit = (instrument) => {
    setEditingInstrument(instrument);
    setFormData({
      tag_number: instrument.tag_number || '',
      name: instrument.name || '',
      instrument_type: instrument.instrument_type || '',
      description: instrument.description || '',
      manufacturer: instrument.manufacturer || '',
      model: instrument.model || '',
      range: instrument.range || '',
      parent_type: instrument.parent_type || 'asset',
      parent_id: instrument.parent_id || '',
      capturePosition: false, // Don't capture new position when editing
    });
    setIsCreateDialogOpen(true);
  };

  // Get parent name
  const getParentName = (instrument) => {
    if (instrument.parent_type === 'asset') {
      const asset = assets.find(a => a.id === instrument.parent_id);
      return asset?.name || 'Unknown Asset';
    } else if (instrument.parent_type === 'building') {
      const building = buildings.find(b => b.id === instrument.parent_id);
      return building?.name || 'Unknown Building';
    }
    return 'Unassigned';
  };

  // Get icon for instrument type
  const getTypeIcon = (type) => {
    const typeInfo = INSTRUMENTATION_TYPES.find(t => t.value === type);
    const IconComponent = typeInfo?.icon || Gauge;
    return <IconComponent className="h-4 w-4" />;
  };

  return (
    <Sheet open={isOpen} onOpenChange={(open) => !open && onClose()}>
      <SheetContent side="right" className="w-[400px] sm:w-[540px] p-0">
        <SheetHeader className="p-6 pb-4 border-b">
          <div className="flex items-center gap-2">
            <Gauge className="h-5 w-5 text-primary" />
            <SheetTitle>Instrumentation</SheetTitle>
          </div>
          <SheetDescription>
            Manage instruments, sensors, and safety devices
          </SheetDescription>
        </SheetHeader>

        <div className="p-4 border-b space-y-3">
          {/* Search and Filters */}
          <div className="flex gap-2">
            <div className="relative flex-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by tag or name..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="pl-9"
              />
            </div>
          </div>
          <div className="flex gap-2">
            <Select value={typeFilter} onValueChange={setTypeFilter}>
              <SelectTrigger className="flex-1">
                <Filter className="h-4 w-4 mr-2" />
                <SelectValue placeholder="Type" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                {INSTRUMENTATION_TYPES.map(type => (
                  <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            <Select value={parentFilter} onValueChange={setParentFilter}>
              <SelectTrigger className="flex-1">
                <SelectValue placeholder="Parent" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Parents</SelectItem>
                <SelectItem value="asset">Assets</SelectItem>
                <SelectItem value="building">Buildings</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Add Button */}
          {canEdit && (
            <Button 
              onClick={() => setIsCreateDialogOpen(true)} 
              className="w-full gap-2"
            >
              <Plus className="h-4 w-4" />
              Add Instrument
            </Button>
          )}
        </div>

        {/* Instruments List */}
        <ScrollArea className="h-[calc(100vh-320px)]">
          <div className="p-4 space-y-3">
            {loading ? (
              <div className="flex items-center justify-center py-8">
                <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
              </div>
            ) : filteredInstruments.length === 0 ? (
              <div className="text-center py-8 text-muted-foreground">
                <Gauge className="h-12 w-12 mx-auto mb-3 opacity-50" />
                <p>No instruments found</p>
                {canEdit && (
                  <p className="text-sm mt-1">Click "Add Instrument" to create one</p>
                )}
              </div>
            ) : (
              filteredInstruments.map(instrument => (
                <div 
                  key={instrument.id}
                  className="p-4 rounded-lg border bg-card hover:bg-accent/50 transition-colors"
                >
                  <div className="flex items-start justify-between gap-3">
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        {getTypeIcon(instrument.instrument_type)}
                        <h4 className="font-medium truncate">{instrument.tag_number}</h4>
                        {instrument.instrument_type && (
                          <Badge variant="outline" className="text-xs">
                            {INSTRUMENTATION_TYPES.find(t => t.value === instrument.instrument_type)?.label || instrument.instrument_type}
                          </Badge>
                        )}
                      </div>
                      {instrument.name && (
                        <p className="text-sm text-muted-foreground">{instrument.name}</p>
                      )}
                      <div className="flex items-center gap-2 mt-2 text-xs text-muted-foreground">
                        {instrument.parent_type === 'building' ? (
                          <Building className="h-3 w-3" />
                        ) : (
                          <Package className="h-3 w-3" />
                        )}
                        <span>{getParentName(instrument)}</span>
                      </div>
                      {instrument.range && (
                        <p className="text-xs text-muted-foreground mt-1">Range: {instrument.range}</p>
                      )}
                      {instrument.sweep_id && (
                        <p className="text-xs text-green-600 mt-1 flex items-center gap-1">
                          <MapPin className="h-3 w-3" />
                          Position saved
                        </p>
                      )}
                    </div>
                    <div className="flex items-center gap-1">
                      {instrument.sweep_id && onNavigateToInstrument && (
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => onNavigateToInstrument(instrument.sweep_id, { 
                            rotation: instrument.rotation,
                            transitionTime: 1500 
                          })}
                          title="Go to location"
                        >
                          <Eye className="h-4 w-4" />
                        </Button>
                      )}
                      {canEdit && (
                        <>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleEdit(instrument)}
                          >
                            <Edit3 className="h-4 w-4" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="icon"
                            onClick={() => handleDelete(instrument.id)}
                            className="text-destructive hover:text-destructive"
                          >
                            <Trash2 className="h-4 w-4" />
                          </Button>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))
            )}
          </div>
        </ScrollArea>

        {/* Create/Edit Dialog */}
        <Dialog open={isCreateDialogOpen} onOpenChange={handleCloseDialog}>
          <DialogContent className="max-w-md">
            <DialogHeader>
              <DialogTitle>
                {editingInstrument ? 'Edit Instrument' : 'Add Instrument'}
              </DialogTitle>
              <DialogDescription>
                {editingInstrument ? 'Update instrument details' : 'Add a new instrument'}
              </DialogDescription>
            </DialogHeader>
            <form onSubmit={handleSubmit} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="tag_number">Tag Number *</Label>
                  <Input
                    id="tag_number"
                    value={formData.tag_number}
                    onChange={(e) => setFormData({...formData, tag_number: e.target.value})}
                    placeholder="e.g., PT-101"
                    required
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="instrument_type">Type</Label>
                  <Select 
                    value={formData.instrument_type} 
                    onValueChange={(value) => setFormData({...formData, instrument_type: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select type" />
                    </SelectTrigger>
                    <SelectContent>
                      {INSTRUMENTATION_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="name">Name/Description</Label>
                <Input
                  id="name"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  placeholder="e.g., Inlet Pressure Transmitter"
                />
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="parent_type">Attached To</Label>
                  <Select 
                    value={formData.parent_type} 
                    onValueChange={(value) => setFormData({...formData, parent_type: value, parent_id: ''})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder="Select parent type" />
                    </SelectTrigger>
                    <SelectContent>
                      {PARENT_TYPES.map(type => (
                        <SelectItem key={type.value} value={type.value}>{type.label}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="parent_id">
                    {formData.parent_type === 'building' ? 'Building' : 'Asset'}
                  </Label>
                  <Select 
                    value={formData.parent_id} 
                    onValueChange={(value) => setFormData({...formData, parent_id: value})}
                  >
                    <SelectTrigger>
                      <SelectValue placeholder={`Select ${formData.parent_type}`} />
                    </SelectTrigger>
                    <SelectContent>
                      {formData.parent_type === 'building' 
                        ? buildings.map(b => (
                            <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                          ))
                        : assets.map(a => (
                            <SelectItem key={a.id} value={a.id}>{a.name}</SelectItem>
                          ))
                      }
                    </SelectContent>
                  </Select>
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-2">
                  <Label htmlFor="manufacturer">Manufacturer</Label>
                  <Input
                    id="manufacturer"
                    value={formData.manufacturer}
                    onChange={(e) => setFormData({...formData, manufacturer: e.target.value})}
                    placeholder="e.g., Emerson"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="model">Model</Label>
                  <Input
                    id="model"
                    value={formData.model}
                    onChange={(e) => setFormData({...formData, model: e.target.value})}
                    placeholder="e.g., 3051S"
                  />
                </div>
              </div>

              <div className="space-y-2">
                <Label htmlFor="range">Range/Specs</Label>
                <Input
                  id="range"
                  value={formData.range}
                  onChange={(e) => setFormData({...formData, range: e.target.value})}
                  placeholder="e.g., 0-100 PSI"
                />
              </div>

              {/* Link to 3D Position */}
              {!editingInstrument && (
                <div className="p-3 rounded-lg border space-y-2">
                  <div className="flex items-center justify-between">
                    <Label className="flex items-center gap-2">
                      <MapPin className="h-4 w-4 text-primary" />
                      Link to 3D Position
                    </Label>
                    <Button
                      type="button"
                      variant={formData.capturePosition ? "default" : "outline"}
                      size="sm"
                      onClick={() => setFormData(prev => ({ ...prev, capturePosition: !prev.capturePosition }))}
                    >
                      {formData.capturePosition ? 'Enabled' : 'Disabled'}
                    </Button>
                  </div>
                  {formData.capturePosition && currentSweep?.id && (
                    <p className="text-xs text-green-600">
                      ✓ Current position will be saved
                    </p>
                  )}
                  {formData.capturePosition && !currentSweep?.id && (
                    <p className="text-xs text-yellow-600">
                      ⚠️ Navigate in the 3D viewer to capture position
                    </p>
                  )}
                </div>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={handleCloseDialog}>
                  Cancel
                </Button>
                <Button type="submit">
                  {editingInstrument ? 'Update' : 'Create'}
                </Button>
              </DialogFooter>
            </form>
          </DialogContent>
        </Dialog>
      </SheetContent>
    </Sheet>
  );
}

export default InstrumentationPanel;
